import React, { createContext, useMemo, useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../authentication';
import { useNotifications } from '../../../providers';
import { useOrganizations, useUsers } from '../../organization/providers';
import {
  useGetSpacesQuery,
  useCreateSpaceMutation,
  useUpdateSpaceMutation,
  useRefreshSpaceMutation,
  useDeleteSpaceMutation,
  useDeleteShareMutation,
  useShare
} from '../hooks';
import { Space } from '../models';

const SpacesContext = createContext();

const SpacesProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { addNotification } = useNotifications();
  const { activeUsers, currentUser } = useUsers();
  const { spaceId } = useParams();
  const { organizationId } = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const spacesQuery = useGetSpacesQuery(accessToken, currentOrganization?.id);

  const createSpaceMutation = useCreateSpaceMutation(accessToken, currentOrganization?.id);
  const updateSpaceMutation = useUpdateSpaceMutation(accessToken, currentOrganization?.id);
  const refreshSpaceMutation = useRefreshSpaceMutation(accessToken, currentOrganization?.id);
  const deleteSpaceMutation = useDeleteSpaceMutation(accessToken, currentOrganization?.id);
  const deleteShareMutation = useDeleteShareMutation(accessToken, currentOrganization?.id);

  useEffect(() => {
    if (!organizationId && spaceId) {
      navigate(`/spaces/${spaceId}?organization_id=${currentOrganization?.id}`);
    }
  }, [organizationId, currentOrganization?.id, navigate, spaceId]);

  const currentSpace = useMemo(() => {
    const space = spacesQuery.data?.find((s) => s.id === spaceId);
    return space ? new Space(space) : null;
  }, [spaceId, spacesQuery.data]);

  const participants = useMemo(
    () =>
      activeUsers.filter((user) =>
        currentSpace?.shares[0]?.participants.some((participant) => participant.userId === user.id)
      ),
    [currentSpace, activeUsers]
  );

  const users = useMemo(
    () =>
      activeUsers.filter(
        (user) =>
          !currentSpace?.shares[0]?.participants.some(
            (participant) => participant.userId === user.id
          )
      ),
    [currentSpace, activeUsers]
  );

  const { onShare } = useShare(users, participants, currentSpace, currentUser);
  const handleShare = () => onShare(spaceId, 'space');

  const leaveSpace = useCallback(
    async (id) => {
      const queryKey = ['chat', 'spaces', currentOrganization.id];
      const oldSpace = queryClient.getQueryData(queryKey).find((space) => space.id === id);

      try {
        const currentUserShare = new Space(oldSpace).getShare(currentUser.id);

        await deleteShareMutation.mutateAsync(currentUserShare.id);

        queryClient.setQueryData(queryKey, (oldData) => oldData.filter((space) => space.id !== id));

        navigate(`/spaces`);

        addNotification({
          icon: 'exit',
          message: `You left ${oldSpace.title} space`,
          highlight: oldSpace.title
        });
      } catch (error) {
        addNotification({
          icon: 'alert',
          message: `Couldn't leave ${oldSpace.title} space`,
          highlight: oldSpace.title
        });
      }
    },
    [currentUser, currentSpace, deleteShareMutation, currentOrganization?.id]
  );

  const deleteSpace = useCallback(
    async (id) => {
      const queryKey = ['chat', 'spaces', currentOrganization.id];
      const oldSpace = queryClient.getQueryData(queryKey).find((space) => space.id === id);

      try {
        await deleteSpaceMutation.mutateAsync({ id });

        queryClient.setQueryData(queryKey, (oldData) => oldData.filter((space) => space.id !== id));

        navigate(`/spaces`);

        addNotification({
          icon: 'trash',
          message: `Space ${oldSpace.title} has been deleted`,
          highlight: oldSpace.title
        });
      } catch (error) {
        addNotification({
          icon: 'alert',
          message: `Failed to delete ${oldSpace.title} space `,
          highlight: oldSpace.title
        });
      }
    },
    [deleteSpaceMutation, spacesQuery, addNotification, currentOrganization?.id]
  );

  const createSpace = useCallback(
    async (title, showNotification = true) => {
      try {
        const queryKey = ['chat', 'spaces', currentOrganization?.id];
        const newSpace = await createSpaceMutation.mutateAsync({ title });

        queryClient.setQueryData(queryKey, (oldData) => [...oldData, newSpace.data]);

        if (showNotification) {
          addNotification({
            icon: 'plus',
            message: `Created new space ${title}`,
            highlight: title
          });
        }

        return newSpace.data;
      } catch (error) {
        addNotification({
          type: 'error',
          message: `Failed to create space: ${title}`
        });
      }
      return null;
    },
    [createSpaceMutation, currentOrganization?.id, addNotification]
  );

  const updateSpace = useCallback(
    async (id, data) => {
      try {
        queryClient.setQueryData(['chat', 'spaces', currentOrganization?.id], (oldData) =>
          oldData.map((space) => {
            if (space.id === id) {
              return { ...space, ...data };
            }
            return space;
          })
        );

        await updateSpaceMutation.mutateAsync({ id, data });
      } catch (error) {
        addNotification({
          type: 'error',
          message: `Failed to update space`
        });
      }
    },
    [updateSpaceMutation, currentOrganization?.id, spacesQuery, addNotification]
  );

  const refreshSpace = useCallback(
    async (id) => {
      const queryKey = ['chat', 'questions', 'assets', currentOrganization?.id, id];

      queryClient.setQueryData(queryKey, (oldData) =>
        oldData.map((question) => ({
          ...question,
          status: 'REFRESHING'
        }))
      );

      await refreshSpaceMutation.mutateAsync(id);

      queryClient.setQueryData(['chat', 'spaces', currentOrganization?.id], (oldData) =>
        oldData.map((space) => {
          if (space.id === id) {
            return { ...space, updatedAt: new Date().toISOString() };
          }
          return space;
        })
      );
    },
    [refreshSpaceMutation]
  );

  const value = useMemo(
    () => ({
      spaces: (spacesQuery.data || []).map((space) => new Space(space)),
      currentSpace,
      createSpace,
      updateSpace,
      refreshSpace,
      deleteSpace,
      leaveSpace,
      handleShare
    }),
    [
      spacesQuery.data,
      currentSpace,
      createSpace,
      updateSpace,
      deleteSpace,
      leaveSpace,
      refreshSpace,
      handleShare
    ]
  );

  if (spacesQuery.isInitialLoading) return false;

  if (!currentSpace && spaceId) {
    return <Navigate to='/spaces' />;
  }
  return <SpacesContext.Provider value={value}>{children}</SpacesContext.Provider>;
};

SpacesProvider.propTypes = {
  children: PropTypes.node
};

const useSpaces = () => useContext(SpacesContext);

export { SpacesProvider, SpacesContext, useSpaces };
